import {
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { VexConfigService } from '@vex/config/vex-config.service';
import { filter, map, startWith, switchMap } from 'rxjs/operators';
import { NavigationService } from '../../../core/navigation/navigation.service';
import { VexPopoverService } from '@vex/components/vex-popover/vex-popover.service';
import { MegaMenuComponent } from './mega-menu/mega-menu.component';
import { Observable, of } from 'rxjs';
import { NavigationComponent } from '../navigation/navigation.component';
import { ToolbarUserComponent } from './toolbar-user/toolbar-user.component';
import { ToolbarNotificationsComponent } from './toolbar-notifications/toolbar-notifications.component';
import { NavigationItemComponent } from '../navigation/navigation-item/navigation-item.component';
import { MatMenuModule } from '@angular/material/menu';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NavigationItem } from '../../../core/navigation/navigation-item.interface';
import { checkRouterChildsData } from '@vex/utils/check-router-childs-data';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface SizeLogo {
  logoDesktop: string;
  logoMovil: string;
}

@Component({
  selector: 'vex-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    NgIf,
    RouterLink,
    MatMenuModule,
    NgClass,
    NgFor,
    NavigationItemComponent,
    ToolbarNotificationsComponent,
    ToolbarUserComponent,
    NavigationComponent,
    AsyncPipe
  ]
})
export class ToolbarComponent implements OnInit, OnDestroy {
  /* =========================== ATRIBUTES =========================== */
  @HostBinding('class.shadow-b')
  showShadow: boolean = false;

  // Variable para controlar la visibilidad del logo en móvil
  showLogoInMobile: boolean = false;

  navigationItems$: Observable<NavigationItem[]> =
    this.navigationService.items$;

  isHorizontalLayout$: Observable<boolean> = this.configService.config$.pipe(
    map((config) => config.layout === 'horizontal')
  );
  isVerticalLayout$: Observable<boolean> = this.configService.config$.pipe(
    map((config) => config.layout === 'vertical')
  );
  isNavbarInToolbar$: Observable<boolean> = this.configService.config$.pipe(
    map((config) => config.navbar.position === 'in-toolbar')
  );
  isNavbarBelowToolbar$: Observable<boolean> = this.configService.config$.pipe(
    map((config) => config.navbar.position === 'below-toolbar')
  );
  userVisible$: Observable<boolean> = this.configService.config$.pipe(
    map((config) => config.toolbar.user.visible)
  );
  title$: Observable<string> = this.configService.select(
    (config) => config.sidenav.title
  );

  // Observables para diferentes tamaños de pantalla
  isMobile$: Observable<boolean> = this.createMediaQueryObservable('(max-width: 425px)');
  isTablet$: Observable<boolean> = this.createMediaQueryObservable('(min-width: 426px) and (max-width: 768px)');
  isLaptop$: Observable<boolean> = this.createMediaQueryObservable('(min-width: 769px) and (max-width: 1024px)');

  imgLogo: string = '';
  sizeLogos: SizeLogo = {
    logoDesktop: './assets/img/logo/logogobant.svg',
    logoMovil: './assets/img/logo/Logo_gobernación_movil.svg',
  };

  isDesktop$: Observable<boolean> = this.layoutService.isDesktop$;
  megaMenuOpen$: Observable<boolean> = of(false);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  // Referencia al listener para poder limpiarlo
  private resizeListener: (() => void) | null = null;

  /* ========================== CONSTRUCTOR ========================== */
  constructor(
    private readonly layoutService: VexLayoutService,
    private readonly configService: VexConfigService,
    private readonly navigationService: NavigationService,
    private readonly popoverService: VexPopoverService,
    private readonly router: Router
  ) {}

  /* ============================ METHODS ============================ */
  /* --------------------- Meth. Lifecycle Hooks --------------------- */
  ngOnInit() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.showShadow = checkRouterChildsData(
          this.router.routerState.root.snapshot,
          (data) => data.toolbarShadowEnabled ?? false
        );
      });

    // Configuración del logo y listeners
    this.setupLogo();

    // Suscripción al estado del sidenav para poder actualizar la UI si es necesario
    this.layoutService.sidenavOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Aquí podrías actualizar alguna variable si necesitas
        // reaccionar a la apertura/cierre del sidenav
      });
  }

  ngOnDestroy() {
    // Limpieza del event listener al destruir el componente
    this.removeResizeListener();
  }

  /* -------------------------- Meth. HTML -------------------------- */
  openQuickpanel(): void {
    this.layoutService.openQuickpanel();
  }

  openSidenav(): void {
    this.layoutService.openSidenav();
  }

  openMegaMenu(origin: ElementRef | HTMLElement): void {
    this.megaMenuOpen$ = of(
      this.popoverService.open({
        content: MegaMenuComponent,
        origin,
        offsetY: 12,
        position: [
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top'
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top'
          }
        ]
      })
    ).pipe(
      switchMap((popoverRef) => popoverRef.afterClosed$.pipe(map(() => false))),
      startWith(true)
    );
  }

  openSearch(): void {
    this.layoutService.openSearch();
  }

  /* ----------------------- Métodos Privados ----------------------- */
  private setupLogo(): void {
    // Establece el logo inicial
    this.setLogoImage();

    // Configura el listener de resize
    this.removeResizeListener(); // Por si acaso
    this.resizeListener = () => this.setLogoImage();
    window.addEventListener('resize', this.resizeListener);
  }

  private removeResizeListener(): void {
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }
  }

  /**
   * Crea un Observable que emite cuando cambia el estado de una media query
   */
  private createMediaQueryObservable(query: string): Observable<boolean> {
    const mediaQuery = window.matchMedia(query);
    return new Observable<boolean>(observer => {
      // Emitir valor inicial
      observer.next(mediaQuery.matches);

      // Función para manejar cambios
      const handler = (e: MediaQueryListEvent) => observer.next(e.matches);

      // Añadir listener
      mediaQuery.addEventListener('change', handler);

      // Cleanup cuando se destruye el observable
      return () => mediaQuery.removeEventListener('change', handler);
    });
  }

  setLogoImage(): void {
    const mediaQuery = window.matchMedia('(max-width: 425px)');
    this.imgLogo = mediaQuery.matches
      ? this.sizeLogos.logoMovil
      : this.sizeLogos.logoDesktop;
  }
}
